<template>
  <div style="position:relative;width: 300px" v-clickoutside="clickoutside">
    <span :class="rootCls" style="width:100%" @click="setOpenState(!open)">
      <span
        ref="selection"
        key="selection"
        :class="`${prefixCls}-selection ${prefixCls}-selection--${multiple ? 'multiple' : 'single'}`"
        >
        <template v-if="!multiple">
          <span
            :class="`${prefixCls}-selection__rendered`">
            <span v-if="value$.length" key="value" :title="value$[0].label" :class="`${prefixCls}-selection-selected-value`">{{value$[0].label}}</span>
            <span v-else key="placeholder" :class="`${prefixCls}-selection__placeholder`">{{placeholder}}</span>
          </span>
        </template>
        <template v-else>
          <ul :class="`${prefixCls}-selection__rendered`">
            <li :class="`${prefixCls}-selection__choice`" v-for="singleValue in value$" :key="singleValue.value" :title="singleValue.label">
              <span :class="`${prefixCls}-selection__choice__remove`" @click.prevent.stop="removeSelected(singleValue.value)"></span>
              <span :class="`${prefixCls}-selection__choice__content`">{{singleValue.label}}</span>
            </li>
            <li :class="`${prefixCls}-search ${prefixCls}-search--inline`" key="__input">
              <span :class="`${prefixCls}-search__field__wrap`">
                <input
                  ref="inputRef"
                  v-model="inputValue"
                  :class="`${prefixCls}-search__field`"
                  role="textbox"
                  :style="{width:`${inputRefWidth}px`}"
                  :disabled="disabled"
                  @input="onInputChange"
                />
              </span>
            </li>
          </ul>
        </template>
        <span v-if="allowClear && value$.length && value$[0].value" key="clear" :class="`${prefixCls}-selection__clear`" @click.stop="onClearSelection"></span>
        <span key="arrow" :class="`${prefixCls}-arrow`" style=" outline: 'none'"><b/></span>
        <span
          v-show="multiple && (inputValue!=='' || !value$.length)"
          @click="onPlaceholderClick"
          :class="`${prefixCls}-search__field__placeholder`"
        >
          {{placeholder}}
        </span>
      </span>
    </span>
    <div :class="dropdowmCls" style="max-height: 200px;overflow: auto;" v-show="open">
      <div>
        <span :class="`${this.prefixCls}-dropdown-search`" v-if="!multiple">
          <span :class="`${prefixCls}-search__field__wrap`">
            <input
              ref="inputRef"
              v-model="inputValue"
              :class="`${prefixCls}-search__field`"
              role="textbox"
              :disabled="disabled"
              @input="onInputChange"
            />
            <span
              v-show="!inputValue"
              @click="onPlaceholderClick"
              :class="`${prefixCls}-search__field__placeholder`"
            >
              {{searchPlaceholder}}
            </span>
          </span>
        </span>
        <tree 
          v-if="treedata.length"
          :multiple="multiple"
          :data="treedata"
          :defaultSelectedKeys="defaultSelectedKeys"
          :defaultExpandedKeys="defaultExpandedKeys"
          @onSelect="onSelect"
        ></tree>
        <span v-else :class="`${prefixCls}-not-found`">
          {{notFoundContent}}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
  import classnames from 'classnames'
  import tree from 'v-tree'
  import { toArray, loopAllChildren, flatToHierarchy, deepCopyArray } from './util.js'
  import clickoutside from './clickoutside'
  
  function filterFn (input, child) {
    return child.title.indexOf(input) > -1
  }

  export default {
    components: {
      tree
    },
    directives: {
      clickoutside
    },
    props: {
      data: {
        type: Array,
        default () {
          return []
        }
      },
      value: {
        type: [String, Array]
      },
      prefixCls: {
        type: String,
        default: 'v-tree-select'
      },
      placeholder: {
        type: String,
        default: '请下拉选择'
      },
      searchPlaceholder: {
        type: String,
        default: '请输入搜索'
      },
      showSearch: {
        type: Boolean,
        default: true
      },
      allowClear: {
        type: Boolean,
        default: true
      },
      multiple: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      labelInValue: {   // 是否把每个选项的 label 包装到 value 中，会把 value 类型从 string 变为 {key: string, label: ReactNode, halfChecked(treeCheckStrictly 时有效): string[] } 的格式
        type: Boolean,
        default: false
      },
      filterTreeNode: {
        type: Function,
        default: filterFn
      },
      notFoundContent: {
        type: String,
        default: '抱歉,沒有找到!'
      }
    },
    data () {
      return {
        value$: this.addLabelToValue(this.value),
        inputValue: '',
        open: false,
        focused: false,
        inputRefWidth: 0,
        defaultExpandedKeys: [],
        treedataList: [],
        treedata: deepCopyArray(this.data)
      }
    },
    computed: {
      rootCls () {
        return classnames({
          [this.prefixCls]: true,
          [`${this.prefixCls}-open`]: this.open,
          [`${this.prefixCls}-focused`]: this.open || this.focused,
          [`${this.prefixCls}-disabled`]: this.disabled,
          [`${this.prefixCls}-enabled`]: !this.disabled,
          [`${this.prefixCls}-allow-clear`]: !!this.allowClear
        })
      },
      dropdowmCls () {
        const dropdownPrefixCls = `${this.prefixCls}-dropdown`
        return classnames(dropdownPrefixCls, `${dropdownPrefixCls}--${this.multiple ? 'multiple' : 'single'}`)
      },
      defaultSelectedKeys () {
        return toArray(this.value)
      }
    },
    watch: {
      inputValue (newValue, oldValue) {
        this.inputRefWidth = this.$refs.inputRef.scrollWidth
      },
      value (newVal) {
        this.value$ = this.addLabelToValue(toArray(newVal))
      },
      data (newVal) {
        this.treedata = deepCopyArray(newVal)
      }
    },
    methods: {
      clickoutside () {
        this.setOpenState(false)
      },
      addLabelToValue (value) {
        value = value.map(v => {
          return {
            value: v,
            label: this.getLabel(v)
          }
        })
        return value
      },
      getLabel (value) {
        // 根据value获取label值
        let label = null
        loopAllChildren(this.data, (item) => {
          if (item.key === value) {
            label = item.title
          }
        })
        return label
      },
      onClearSelection () {
        if (this.disabled) {
          return
        }
        if (this.inputValue || this.value.length) {
          this.fireChange([])
          this.setOpenState(false)
          if (this.inputValue === null) {
            this.inputValue = ''
          }
        }
      },
      onInputChange () {
        this.open = true
        this.$emit('onSearch', this.inputValue)

        if (this.inputValue) {
          this.treedata = this.processTreeNode()
        } else {
          this.treedata = this.data
        }
      },
      processTreeNode () {
        const data = deepCopyArray(this.data)
        const filterPoss = []
        this.defaultExpandedKeys = []
        loopAllChildren(data, (child, index, pos) => {
          if (this.filterTreeNode(this.inputValue, child)) {
            filterPoss.push(pos)
            this.defaultExpandedKeys.push(child.key)
          }
        })
  
        // Include the filtered nodes's ancestral nodes.
        const processedPoss = []
        filterPoss.forEach(pos => {
          const arr = pos.split('-')
          arr.reduce((pre, cur) => {
            const res = `${pre}-${cur}`
            if (processedPoss.indexOf(res) < 0) {
              processedPoss.push(res)
            }
            return res
          })
        })
  
        const filterNodesPositions = []
        loopAllChildren(data, (child, index, pos) => {
          if (processedPoss.indexOf(pos) > -1) {
            filterNodesPositions.push({ node: child, pos })
          }
        })
        const hierarchyNodes = flatToHierarchy(filterNodesPositions)
        const recursive = children => {
          return children.map(child => {
            let children
            if (child.children) {
              children = recursive(child.children)
            }
            return Object.assign(child.node, {children})
          })
        }
  
        return recursive(hierarchyNodes)
      },
      onPlaceholderClick () {
        this.$refs.inputRef.focus()
      },
      setOpenState (open, needFocus, documentClickClose = false) {
        this.open = open
        this.$nextTick(() => {
          if (needFocus || open) {
            if (open || this.multiple) {
              const input = this.$refs.inputRef
              if (input && document.activeElement !== input) {
                input.focus()
              }
            } else if (this.$refs.selection) {
              this.$refs.selection.focus()
            }
          }
        })
      },
      onSelect (selectedKeys, info) {
        if (info.selected === false) {
          this.onDeselect(info)
          return
        }
        const item = info.node
        let value = this.value$

        const selectedValue = item.eventKey
        const selectedLabel = item.title
        let event = selectedValue
        // TODO isLabelInValue
        this.$emit('onSelect', event, item, info)
        const checkEvt = info.event === 'check'

        if (this.multiple) {
          // 防重复
          if (value.some(i => i.value === selectedValue)) {
            return
          }
          value = value.concat([{
            value: selectedValue,
            label: selectedLabel
          }])
        } else {
          if (value.length && value[0].value === selectedValue) {
            this.setOpenState(false)
            return
          }
          value = [{
            value: selectedValue,
            label: selectedLabel
          }]
          this.setOpenState(false)
        }
        const extraInfo = {
          triggerValue: selectedValue,
          triggerNode: item
        }
        if (checkEvt) {
  
        } else {
          extraInfo.selected = info.selected
        }

        this.fireChange(value, extraInfo)
        if (this.inputValue === null) {
          this.inputValue = ''
        }
      },
      onDeselect (info) {
        this.removeSelected(info.node.eventKey)

        if (!this.multiple) {
          this.setOpenState(false)
        }
        if (this.inputValue === null) {
          this.inputValue = ''
        }
      },
      removeSelected (selectedVal) {
        if (this.disabled) {
          return
        }
  
        let label
        const value = this.value$.filter((singleValue) => {
          if (singleValue.value === selectedVal) {
            label = singleValue.label
          }
          return (singleValue.value !== selectedVal)
        })

        this.fireChange(value, { clear: true })
      },
      fireChange (value, extraInfo) {
        const vals = value.map(i => i.value)
        const sv = this.value$.map(i => i.value)
        if (vals.length !== sv.length || !vals.every((val, index) => sv[index] === val)) {
          const ex = { preValue: [...this.value$] }
          if (extraInfo) {
            Object.assign(ex, extraInfo)
          }
          let labs = null
          let vls = value
          if (!this.labelInValue) {
            labs = value.map(i => i.label)
            vls = vls.map(v => v.value)
          }

          this._savedValue = this.multiple ? vls : vls[0]
          this.$emit('input', this._savedValue)
          this.$emit('onChange', this._savedValue, labs, ex)
          // 以下代码用watch value代替
          // this.value$ = this.getValue(toArray(this._savedValue).map((v, i) => {
          //   return this.labelInValue ? v : {
          //     value: v,
          //     label: labs && labs[i]
          //   }
          // }))
        }
      },
      getValue (val, init = true) {
        let value = val

        return value
      }
    },
    mounted () {
      this.inputRefWidth = this.$refs.inputRef.scrollWidth
    }
  }
</script>

<style lang="less">
  // @import url(../../assets/index.less);
  @import url(../../assets/ant.less);
  
</style>


// WEBPACK FOOTER //
// src/components/tree-select/tree-select.vue?0a02f914